export type DevParamType = "boolean" | "int" | "string";

export interface DevParamDef {
	key: string;
	label: string;
	description: string;
	type: DevParamType;
	requiresImmediateStart?: boolean;
}

export const DEV_PARAM_DEFS: DevParamDef[] = [
	{
		key: "dev",
		label: "Dev mode",
		type: "boolean",
		description: "Dev loadout, invulnerability, perf stats. Unchecking closes this panel.",
	},
	{
		key: "no_menu",
		label: "Skip menu",
		type: "boolean",
		description: "Wipe the save and start a new game immediately.",
	},
	{
		key: "playground",
		label: "Playground",
		type: "boolean",
		description: "Empty board via PlaygroundLevelGenerator, for testing in isolation.",
	},
	{
		key: "nodark",
		label: "No darkness",
		type: "boolean",
		description: "Disable darkness on the starting board.",
	},
	{
		key: "level",
		label: "Level",
		type: "int",
		description: "Start at level N (empty = 1).",
	},
	{
		key: "seed",
		label: "Seed",
		type: "string",
		description: "Fixed seed for new games (empty = random).",
	},
	{
		key: "auto_restart",
		label: "Auto-restart ms",
		type: "int",
		requiresImmediateStart: true,
		description: "Regenerate the level every N ms; needs skip menu or playground. ESC stops the cycle.",
	},
];

export interface StartupDevParams {
	dev: boolean;
	startLevel: number;
	noMenu: boolean;
	playground: boolean;
	nodark: boolean;
	autoRestartIntervalMs: number;
}

export function parseStartupParams(search: string): StartupDevParams {
	const params = new URLSearchParams(search);
	const dev = params.get("dev") === "true";

	const parsedLevel = parseInt(params.get("level") ?? "", 10);
	const startLevel = !isNaN(parsedLevel) && parsedLevel >= 1 ? parsedLevel : 1;

	const noMenu = params.get("no_menu") === "true";
	const playground = params.get("playground") === "true" && dev;
	const nodark = params.get("nodark") === "true" && dev;

	// auto_restart: `true` → 1000ms, or a positive int in ms.
	let autoRestartIntervalMs = 0;
	if (dev && (noMenu || playground)) {
		const raw = params.get("auto_restart");
		if (raw === "true") {
			autoRestartIntervalMs = 1000;
		} else if (raw !== null) {
			const parsed = parseInt(raw, 10);
			if (!isNaN(parsed) && parsed > 0) {
				autoRestartIntervalMs = parsed;
			}
		}
	}

	return { dev, startLevel, noMenu, playground, nodark, autoRestartIntervalMs };
}

export function isDevMode(): boolean {
	// `window` is undefined in non-browser contexts (e.g. scripts/balance-tool.ts via tsx).
	if (typeof window === "undefined") return false;
	return parseStartupParams(window.location.search).dev;
}

export function getSeedFromUrl(): string | undefined {
	if (typeof window === "undefined") return undefined;
	const seed = new URLSearchParams(window.location.search).get("seed");
	return seed ? seed : undefined;
}

export function withParam(search: string, key: string, value: string | null): string {
	const params = new URLSearchParams(search);
	if (value === null || value === "") {
		params.delete(key);
	} else {
		params.set(key, value);
	}
	const result = params.toString();
	return result ? `?${result}` : "";
}

export function buildSearch(entries: Record<string, string>): string {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(entries)) {
		params.set(key, value);
	}
	const result = params.toString();
	return result ? `?${result}` : "";
}
